import { Action } from "redux";

import { IComponent, ILink, ILoadStateAction, IPicassoState, LOAD_PICASSO_STATE as LOAD_WORKFLOW_ENTITIES } from "@multiweb/picasso";
import { MessageWindowComponentProps, MessageWindowProps } from "@multiweb/reactComponents";
import { TypedObject } from "@multiweb/types/react";
import {
    CLEAR_PICASSO_HOST,
    CREATE_TRANSITION,
    DELETE_SELECTED_WF_ENTITIES,
    DELETE_SET,
    DIALOG_CANCELED,
    DIALOG_CONFIRMED,
    ENABLE_STATUS_VIEW,
    ENABLE_VIEWER_MODE,
    HIDE_MESSAGE_WINDOW,
    INIT_PICASSO_HOST,
    LOAD_ACTIVITY_HEADER,
    LOAD_TRANSITION_HEADER,
    SET_TOUCHED,
    SHOW_MESSAGE_WINDOW,
    TOGGLE_EDIT_DIALOGS_VISIBILITY,
    TOGGLE_GRID,
    UPDATE_ACTIVITY,
    UPDATE_ACTIVITY_PROPERTY_PANEL_STATE,
    UPDATE_DEFINITION_PARTICIPANTS,
    UPDATE_GENERAL_PROPERTY_PANEL_STATE,
    UPDATE_SETS,
    UPDATE_TRANSITION,
    UPDATE_TRANSITION_PROPERTY_PANEL_STATE,
    UPDATE_WORKFLOW_VIEW,
} from "./constants";
import { ActionType, EditDialogType, ParticipantType, TriggerType, WorkflowEditorMode } from "./enums";
import { ActivityType, IOTransitionType } from "./workflowModel";

export type WorkflowEditorProps = {
    readonly hostKey: string;
};

export interface IWorkflowEditorState {
    readonly instances: TypedObject<IHostInstanceState>;
}

export interface IHostInstanceState {
    readonly picasso: IPicassoState;
    readonly wfEditorMode: WorkflowEditorMode;
    readonly isGridEnabled: boolean;
    readonly isEditDialogHidden: boolean;
    readonly touched: boolean;
    readonly selectedSetId: string;
    readonly orderedSetsIds: readonly string[];
    readonly sets: TypedObject<ISet>;
    readonly activities: readonly IActivity[];
    readonly transitions: readonly ITransition[];
    readonly activityPropertyPanelState: IActivityPropertyPanelState;
    readonly transitionPropertyPanelState: ITransitionPropertyPanelState;
    readonly generalPropertyPanelState: IGeneralPropertyPanelState;
    readonly definitionParticipants: readonly IParticipant[];
    readonly variables: readonly IVariable[];
    readonly wfMessageWindowState: WfMessageWindowState;
}

export type WfMessageWindowState = {
    readonly isVisible: boolean;
    readonly actionId: string | null;
    readonly contentLocKey: string | null;
    readonly primaryButtonLocKey: string | null;
    readonly secondaryButtonLocKey: string | null;
};

export type EditDialogState = {
    readonly isOpen: boolean;
    readonly type: EditDialogType | null;
};

/**
 * Stav používaný v komponentě ToolbarPanel.tsx sloužící pro redukci množství potřebných funkncí pro zobrazení MessageBoxu.
 */
export type MessageWindowState = {
    readonly isOpen: boolean;
    readonly properties: Pick<MessageWindowComponentProps, "children" | "onPrimaryButtonClick" | "onCloseMessageWindow"> & Pick<MessageWindowProps, "primaryButtonLocKey" | "secondaryButtonLocKey"> | null;
};

export type ActivityPropertyContainers = "PROPERTIES" | "ACTIONS" | "TRIGGERS" | "PARTICIPANTS" | "ACTIVE_PARTICIPANTS" | "SETS";

export interface IActivityPropertyPanelState {
    readonly "PROPERTIES": {
        readonly isOpen: boolean;
    };
    readonly "ACTIONS": {
        readonly isOpen: boolean;
    };
    readonly "TRIGGERS": {
        readonly isOpen: boolean;
    };
    readonly "PARTICIPANTS": {
        readonly isOpen: boolean;
    };
    readonly "ACTIVE_PARTICIPANTS": {
        readonly isOpen: boolean;
    };
    readonly "SETS": {
        readonly isOpen: boolean;
    };
}

export type TransitionPropertyContainers = "PROPERTIES" | "ACTIONS";

export interface ITransitionPropertyPanelState {
    readonly "PROPERTIES": {
        readonly isOpen: boolean;
    };
    readonly "ACTIONS": {
        readonly isOpen: boolean;
    };
}

export type GeneralPropertyContainer = "VARIABLES" | "LEGEND" | "SET";

export interface IGeneralPropertyPanelState {
    readonly "VARIABLES": {
        readonly isOpen: boolean;
    };
    readonly "LEGEND": {
        readonly isOpen: boolean;
    };
    readonly "SET": {
        readonly isOpen: boolean;
    };
}

export interface ISet {
    readonly id: string;
    readonly name: string;
    readonly description: string;
}

export interface IActivity {
    readonly picassoIndex: number;
    readonly id: string;
    readonly condition: string;
    readonly runCondition: string;
    readonly type: ActivityType;
    readonly order: number;
    readonly killChildren: boolean;
    readonly showInWfHistory: boolean;
    readonly inheritParticipants: boolean;
    readonly inputTransType: IOTransitionType;
    readonly outputTransType: IOTransitionType;
    readonly description: string;
    readonly name: string;
    readonly isActive: boolean;
    readonly header?: string;

    readonly actions: readonly IAction[];
    readonly participants: readonly IParticipant[];
    readonly activeParticipants: readonly IParticipant[];
    readonly triggers: readonly ITrigger[];
    readonly positions: readonly IPosition[];
}

export interface ITransition {
    readonly picassoIndex: number;
    readonly id: string;
    readonly name: string;
    readonly toIndex: number;
    readonly fromIndex: number;
    readonly condition: string;
    readonly setId: string;
    readonly isActive: boolean;
    readonly header?: string;
    readonly actions: readonly IAction[];
}

export interface IAction {
    readonly actionType: ActionType;
    readonly locKey: string;
    readonly name: string;
    readonly properties: TypedObject<string>;
}

export interface IParticipant {
    readonly identifier: string;
    readonly participantType: ParticipantType;
    readonly locKey: string;
    readonly name: string;
    readonly properties: TypedObject<string>;
}

export interface ITrigger {
    readonly triggerType: TriggerType;
    readonly locKey: string;
    readonly name: string;
    readonly properties: TypedObject<string>;
}

export interface IVariable {
    readonly name: string;
    readonly value: string;
}

export interface IPosition {
    readonly setId: string;
    readonly setName: string;
}

/** Typ popisující propojení, které přísluší k aktivitě. */
export type ActivityTransition = {
    readonly transitionName: string;
    readonly transitionCondition: string;
    readonly otherActivityName: string;
};

export interface IPicassoHostAction extends Action<string> {
    readonly meta: {
        readonly hostKey: string;
    };
}

export class IPicassoHostAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance?.meta?.hostKey;
    }
}

export interface IInitPicassoHostAction extends IPicassoHostAction {}

export class IInitPicassoHostAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === INIT_PICASSO_HOST;
    }
}

export interface IClearPicassoHostAction extends IPicassoHostAction {}

export class IClearPicassoHostAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLEAR_PICASSO_HOST;
    }
}

export interface IEnableStatusViewAction extends IPicassoHostAction {
    readonly payload: {
        readonly variables: readonly IVariable[];
    };
}

export class IEnableStatusViewAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === ENABLE_STATUS_VIEW;
    }
}

export interface IEnableViewerModeAction extends IPicassoHostAction { }

export class IEnableViewerModeAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === ENABLE_VIEWER_MODE;
    }
}

export interface IToggleGridAction extends IPicassoHostAction {}

export class IToggleGridAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === TOGGLE_GRID;
    }
}

export interface IToggleEditDialogsVisibilityAction extends IPicassoHostAction {}

export class IToggleEditDialogsVisibilityAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === TOGGLE_EDIT_DIALOGS_VISIBILITY;
    }
}

export interface ISetTouchedAction extends IPicassoHostAction {
    readonly payload: {
        readonly touched: boolean;
    };
}

export class ISetTouchedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_TOUCHED;
    }
}

export interface ILoadWorkflowEntities extends ILoadStateAction, IPicassoHostAction {
    readonly meta: {
        readonly hostKey: string;
        readonly picassoInstanceKey: string;
    };
    readonly payload: {
        readonly selectedSetId?: string;
        readonly selectedComponentIndex?: number;
        readonly selectedLinkIndex?: number;
        readonly sets: readonly ISet[];
        readonly activities: readonly IActivity[];
        readonly transitions: readonly ITransition[];
        readonly components: readonly IComponent[];
        readonly links: readonly ILink[];
        readonly definitionParticipants: readonly IParticipant[];
    };
}

export class ILoadWorkflowEntities {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null
            && instance.type === LOAD_WORKFLOW_ENTITIES
            && instance.payload?.activities && instance.payload?.transitions
            && instance instanceof IPicassoHostAction;
    }
}

export interface IUpdateSetsAction extends IPicassoHostAction {
    readonly payload: {
        readonly newSets: readonly ISet[];
    };
}

export class IUpdateSetsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_SETS;
    }
}

export interface IUpdateActivityAction extends IPicassoHostAction {
    readonly payload: {
        readonly picassoIndex: number;
        readonly newActivity: IActivity;
    };
}

export class IUpdateActivityAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_ACTIVITY;
    }
}

export interface IUpdateTransitionAction extends IPicassoHostAction {
    readonly payload: {
        readonly index: number;
        readonly newTransition: ITransition;
    };
}

export class IUpdateTransitionAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_TRANSITION;
    }
}

export interface IUpdateActivityPropertyPanelStateAction extends IPicassoHostAction {
    readonly payload: {
        readonly containerName: ActivityPropertyContainers;
        readonly isExpanded: boolean;
    };
}

export class IUpdateActivityPropertyPanelStateAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_ACTIVITY_PROPERTY_PANEL_STATE;
    }
}

export interface IUpdateTransitionPropertyPanelStateAction extends IPicassoHostAction {
    readonly payload: {
        readonly containerName: TransitionPropertyContainers;
        readonly isExpanded: boolean;
    };
}

export class IUpdateTransitionPropertyPanelStateAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_TRANSITION_PROPERTY_PANEL_STATE;
    }
}

export interface IUpdateGeneralPropertyPanelStateAction extends IPicassoHostAction {
    readonly payload: {
        readonly containerName: GeneralPropertyContainer;
        readonly isExpanded: boolean;
    };
}

export class IUpdateGeneralPropertyPanelStateAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_GENERAL_PROPERTY_PANEL_STATE;
    }
}

export interface IUpdateDefinitionParticipantsAction extends IPicassoHostAction {
    readonly payload: {
        readonly newParticipants: readonly IParticipant[];
        readonly newActivities?: readonly IActivity[];
    };
}

export class IUpdateDefinitionParticipantsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_DEFINITION_PARTICIPANTS;
    }
}

export interface IUpdateWorkflowViewAction extends IPicassoHostAction {
    readonly payload: {
        readonly setId: string;
        readonly selectedActivityId?: string;
        readonly selectedTransitionId?: string;
        readonly isNonEditChange: boolean;
    };
}

export class IUpdateWorkflowViewAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === UPDATE_WORKFLOW_VIEW;
    }
}

export interface ICreateTransitionAction extends IPicassoHostAction { }

export class ICreateTransitionAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CREATE_TRANSITION;
    }
}

export interface IDeleteSelectedWfEntitiesAction extends IPicassoHostAction { }

export class IDeleteSelectedWfEntitiesAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === DELETE_SELECTED_WF_ENTITIES;
    }
}

export interface IDeleteSetAction extends IPicassoHostAction { }

export class IDeleteSetAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === DELETE_SET;
    }
}

export interface ILoadActivityHeaderAction extends IPicassoHostAction {
    readonly payload: {
        readonly picassoIndex: number;
        readonly wfStateCodes: readonly string[];
    };
}

export class ILoadActivityHeaderAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_ACTIVITY_HEADER;
    }
}

export interface ILoadTransitionHeaderAction extends IPicassoHostAction {
    readonly payload: {
        readonly picassoIndex: number;
        readonly wfStateCodes: readonly string[];
    };
}

export class ILoadTransitionHeaderAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_TRANSITION_HEADER;
    }
}

export interface IShowMessageWindowAction extends IPicassoHostAction {
    readonly payload: {
       readonly actionId: string;
       readonly primaryButtonLocKey: string;
       readonly secondaryButtonLocKey: string;
       readonly contentLocKey: string;
    };
}

export class IShowMessageWindowAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SHOW_MESSAGE_WINDOW;
    }
}

export interface IHideMessageWindowAction extends IPicassoHostAction { }

export class IHideMessageWindowAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === HIDE_MESSAGE_WINDOW;
    }
}

export interface IDialogConfirmedAction extends IPicassoHostAction {
    readonly payload: {
        readonly actionId: string;
    };
}

export class IDialogConfirmedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === DIALOG_CONFIRMED;
    }
}

export interface IDialogCanceledAction extends IPicassoHostAction {
    readonly payload: {
        readonly actionId: string;
    };
}

export class IDialogCanceledAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === DIALOG_CANCELED;
    }
}
